import { supabase } from '../supabase';

export interface LeaderboardEntry {
  rank: number;
  user_id: string;
  display_name: string;
  total_xp: number;
  lessons_completed: number;
  streak: number;
  league: string;
}

export interface UserRankInfo {
  user_rank: number;
  total_xp: number;
  next_user_name: string | null;
  next_user_xp: number | null;
  xp_needed: number | null;
  total_users: number;
}

/**
 * Sync user XP and streak from localStorage to database before updating ranking
 */
const syncUserDataToDatabase = async (userId: string): Promise<void> => {
  try {
    // Get localStorage data
    const progressKey = 'userProgress';
    const existingProgress = localStorage.getItem(progressKey);
    
    if (!existingProgress) return;
    
    const progress = JSON.parse(existingProgress);
    
    // 1. Sync user stats to database
    // First check if user exists
    const { data: existingUser } = await supabase
      .from('users')
      .select('id, email')
      .eq('id', userId)
      .single();

    if (existingUser) {
      // Update existing user
      await supabase
        .from('users')
        .update({
          streak: progress.streak || 0,
          xp: progress.totalXP || 0,
          last_login: new Date().toISOString()
        })
        .eq('id', userId);
    } else {
      // Create new user (get email from auth)
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        await supabase
          .from('users')
          .insert({
            id: userId,
            email: authUser.email,
            streak: progress.streak || 0,
            xp: progress.totalXP || 0,
            display_name: authUser.user_metadata?.full_name || authUser.user_metadata?.name || authUser.email?.split('@')[0],
            last_login: new Date().toISOString()
          });
      }
    }
    
    // 2. DON'T sync old quiz attempts from localStorage
    // This prevents old quizzes from appearing as "completed this week"
    // New quiz attempts will be saved directly when completed (in QuizScreen)
    console.log('ℹ️ Skipping quiz attempts sync to prevent timestamp issues');
    
    console.log('✅ User XP synced to database:', progress.totalXP);
  } catch (error) {
    console.error('Error syncing user data:', error);
  }
};

/**
 * Update user's weekly ranking
 */
export const updateWeeklyRanking = async (userId: string): Promise<void> => {
  try {
    // First sync user data from localStorage to database
    await syncUserDataToDatabase(userId);
    
    // Then update weekly ranking
    const { error } = await supabase.rpc('update_weekly_ranking', {
      p_user_id: userId
    });

    if (error) {
      console.error('Error updating weekly ranking:', error);
      throw error;
    }
    
    console.log('✅ Weekly ranking updated');
  } catch (error) {
    console.error('Failed to update weekly ranking:', error);
  }
};

/**
 * Get weekly leaderboard
 */
export const getWeeklyLeaderboard = async (limit: number = 10): Promise<LeaderboardEntry[]> => {
  try {
    const { data, error } = await supabase.rpc('get_weekly_leaderboard', {
      p_limit: limit
    });

    if (error) {
      console.error('Error fetching leaderboard:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Failed to fetch leaderboard:', error);
    return [];
  }
};

/**
 * Get user's rank and position
 */
export const getUserRank = async (userId: string): Promise<UserRankInfo | null> => {
  try {
    const { data, error } = await supabase.rpc('get_user_rank', {
      p_user_id: userId
    });

    if (error) {
      console.error('Error fetching user rank:', error);
      return null;
    }

    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Failed to fetch user rank:', error);
    return null;
  }
};

/**
 * Get league emoji based on league name
 */
export const getLeagueEmoji = (league: string): string => {
  switch (league?.toLowerCase()) {
    case 'diamond':
      return '💎';
    case 'gold':
      return '🥇';
    case 'silver':
      return '🥈';
    case 'bronze':
      return '🥉';
    default:
      return '🏆';
  }
};

/**
 * Get league color based on league name
 */
export const getLeagueColor = (league: string): string => {
  switch (league?.toLowerCase()) {
    case 'diamond':
      return '#B9F2FF';
    case 'gold':
      return '#FFD700';
    case 'silver':
      return '#C0C0C0';
    case 'bronze':
      return '#CD7F32';
    default:
      return '#4CAF50';
  }
};

/**
 * Get rank medal emoji
 */
export const getRankMedal = (rank: number): string => {
  switch (rank) {
    case 1:
      return '🥇';
    case 2:
      return '🥈';
    case 3:
      return '🥉';
    default:
      return '';
  }
};

/**
 * Format display name (show full name)
 */
export const formatDisplayName = (name: string): string => {
  if (!name) return 'Anonymous';
  return name.trim();
};

